import { ProductTier } from '@sparemin/api-sdk';
import { RejectedReason } from 'components/FileUploader';
import { MediaImportedArgs } from 'containers/MediaUploadModal/types';
import { getProjectCreationMethod } from 'containers/ProjectWizards/VideoWizard/utils';
import { IVideoUpload } from 'redux/middleware/api/media-upload-service';
import * as mixpanelActions from 'redux/modules/mixpanel/actions';
import { OnWizardNextProps } from 'redux/modules/mixpanel/types';
import { pushModal } from 'redux/modules/modal';
import { showError } from 'redux/modules/notification';
import { tierSelector } from 'redux/modules/pricing';
import { goToCreate, goToProject } from 'redux/modules/router';
import {
  entireVideoEnhancer,
  uploadVideoToStorage,
  VideoUploadArgs,
} from 'redux/modules/video-upload';
import { createVideoTranscriptionProject } from 'redux/modules/video-wizard/actions';
import {
  captionSourceEddyProjectCuidSelector,
  projectIdSelector,
} from 'redux/modules/wizard-export/selectors';
import { ThunkAction } from 'redux/types';
import { IVideoClip, Layerable } from 'types';
import { ApplicationError } from 'utils/ApplicationError';
import { DropZoneType, UPLOAD_VIDEO_MAX_FILE_SIZE_MB } from 'utils/constants';
import { getSourceDurationMillisFromVideoEntity } from 'utils/embed/video';
import { ERROR_ID, MIXPANEL_WIZARD_STEP_MAP } from './constants';
import {
  VideoFlowStep,
  VideoFlowSubmitConfig,
  VideoFlowUploadArgs,
} from './types';
import { createUploadArgs } from './utils';

export const trackStepChange = (
  step: VideoFlowStep,
  props?: OnWizardNextProps,
): ThunkAction<void> => dispatch => {
  const mixpanelStep = MIXPANEL_WIZARD_STEP_MAP[step];

  if (!mixpanelStep) {
    // no tracking defined for this step
    return;
  }

  dispatch(
    mixpanelActions.onWizardNext({
      step: mixpanelStep,
      type: 'videoTranscription',
      ...props,
    }),
  );
};

export type VideoFlowErrorHandlingResult = 'handled' | 'unhandled';

export const videoFlowError = (
  err: ApplicationError,
  file?: File,
  reason?: RejectedReason,
): ThunkAction<VideoFlowErrorHandlingResult> => (dispatch, getState) => {
  if (file) {
    dispatch(
      mixpanelActions.onFileUpload({
        dropZoneType: DropZoneType.VIDEO_TRANSCRIPTION,
        source: 'localFile',
        file,
        error: err.message,
      }),
    );
  }

  const tier = tierSelector(getState());
  const size = file?.size ? file.size / 1024 ** 2 : undefined;

  if (
    reason === RejectedReason.SIZE_LIMIT_EXCEEDED &&
    tier === ProductTier.FREE &&
    size !== undefined &&
    size < UPLOAD_VIDEO_MAX_FILE_SIZE_MB
  ) {
    dispatch(
      pushModal({
        name: 'FileSizeUpsell',
        params: {
          variant: 'file',
        },
      }),
    );
    return 'handled';
  }

  return 'unhandled';
};

const updateMainVideoInfo = (
  videoClips: Layerable[],
  uploadedVideo: IVideoUpload,
): Partial<IVideoClip> => ({
  ...videoClips[0], // Main video is always in the first position.
  assetType: 'video',
  sourceDurationMilli: getSourceDurationMillisFromVideoEntity(uploadedVideo),
  videoId: uploadedVideo?.id,
  videoUrl: uploadedVideo?.transcodedVideoUrl,
  previewThumbnail: uploadedVideo?.previewThumbnail?.thumbnails?.find(
    thumbnail => thumbnail?.type === 'firstFrame',
  ),
});

const handleVideoUpload = (
  args: Partial<VideoFlowUploadArgs>,
  mediaImportedArgs?: MediaImportedArgs,
): ThunkAction<Promise<IVideoUpload>> => async dispatch => {
  try {
    const {
      durationMillis,
      language,
      trimEndMillis,
      trimStartMillis,
      waitForUpload = false,
    } = args;

    const videoEntity = await dispatch(
      uploadVideoToStorage(
        {
          ...args,
          language,
          trimStartMillis: trimStartMillis !== 0 ? trimStartMillis : undefined,
          trimEndMillis:
            trimEndMillis !== durationMillis ? trimEndMillis : undefined,
          initiateProcessing: true,
        },
        mediaImportedArgs,
        { waitForUpload },
      ),
    );

    return videoEntity?.toJS();
  } catch {
    dispatch(
      showError({
        message: 'Video upload failed',
        code: 'ER003',
        id: ERROR_ID,
      }),
    );

    dispatch(goToCreate());

    return undefined;
  }
};

const createProject = (
  args: Partial<VideoFlowUploadArgs>,
  projectName: string,
  mediaImportedArgs?: MediaImportedArgs,
): ThunkAction<void> => async (dispatch, getState) => {
  const {
    aspectRatioName,
    src,
    durationMillis,
    trimStartMillis,
    trimEndMillis,
    language,
    isAutoFrame,
    initiateExport,
    isClipSuggestion,
    isFullEpisodeExport,
    clipSuggestionLengthSecs,
    transcribe,
    exportState,
    useYoutubeMostReplayed,
  } = args;
  const captionSourceEddyProjectCuid = captionSourceEddyProjectCuidSelector(
    getState(),
  );

  const entireUploadedVideo = entireVideoEnhancer.selectors.entireUploadedVideoSelector(
    getState(),
  );

  let uploadedVideoId: number | undefined;
  let mainVideo: Partial<IVideoClip> | undefined;

  if (entireUploadedVideo) {
    uploadedVideoId = entireUploadedVideo?.id;
    mainVideo = updateMainVideoInfo(
      exportState?.videoClips,
      entireUploadedVideo,
    );
  } else {
    const uploadedVideo = await dispatch(
      handleVideoUpload(
        {
          ...args,
          // As per the comment here: https://sparemin.atlassian.net/browse/SPAR-24905?focusedCommentId=53689
          // clip suggestion projects required the uploaded video trasncript to be enabled. If not, the later
          // export will fail. For this reason, the transcribe value wis forsed to true when these kind
          // of projects are exported.
          transcribe: isClipSuggestion ? true : transcribe,
        },
        mediaImportedArgs,
      ),
    );

    mainVideo = updateMainVideoInfo(exportState?.videoClips, uploadedVideo);
    uploadedVideoId = Number(uploadedVideo?.id);
  }

  dispatch(
    createVideoTranscriptionProject({
      aspectRatioName,
      videoSrc: !uploadedVideoId ? src : undefined,
      durationMillis,
      trimStartMillis,
      trimEndMillis,
      transcription: {
        transcribe,
        language,
      },
      uploadedVideoId,
      isAutoFrame,
      initiateExport,
      isClipSuggestion,
      isFullEpisodeExport,
      clipSuggestionLengthSecs,
      projectName,
      enableBlur: isClipSuggestion,
      exportState: {
        ...exportState,
        videoClips: [
          mainVideo as Layerable,
          ...exportState.videoClips.slice(1),
        ],
      },
      isVideoConfigLocked: true,
      projectCreatMethod: getProjectCreationMethod(
        isClipSuggestion,
        isFullEpisodeExport,
      ),
      useYoutubeMostReplayed,
      captionSourceEddyProjectCuid,
    }),
  ).catch(() => {
    dispatch(
      showError({
        message: 'There was an error creating your project.',
        code: 'ER009',
        id: ERROR_ID,
      }),
    );
  });
};

const onCompleteWizardTracking = (
  args: VideoUploadArgs,
): ThunkAction<void> => dispatch => {
  const clipDurationInMillis = args.trimEndMillis - args.trimStartMillis;

  dispatch(
    mixpanelActions.onCompleteWizard({
      aspectRatio: args.aspectRatioName,
      audioSource: 'upload',
      clipDuration: args.durationMillis,
      clipEnd: args.trimEndMillis,
      clipped: args.durationMillis - clipDurationInMillis !== 0,
      clipStart: args.trimStartMillis,
      transcription: true,
      type: 'videoTranscription',
      platform: args.platform ?? 'None',
      videoPodcastType: args.videoPodcastType,
      videoType: args.videoType?.id ?? 'None',
    }),
  );
};

export const videoFlowSubmit = (
  config: VideoFlowSubmitConfig,
): ThunkAction<void> => dispatch => {
  const { projectName, mediaImportedArgs } = config;

  const args = createUploadArgs(config);

  dispatch(onCompleteWizardTracking(args));
  return dispatch(createProject(args, projectName, mediaImportedArgs));
};

export const openNewProject = (): ThunkAction<void> => (dispatch, getState) => {
  const projectId = projectIdSelector(getState());
  dispatch(goToProject({ projectId, history: false, fromWizard: true }));
};
